import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import dynamic from 'next/dynamic';
import type { ReactElement } from 'react';
import { useCallback, useRef, useState } from 'react';
import type { Chain } from 'viem';

import type { SelectTokenFilter } from '@/components/SelectTokenDialog/types';

import { dialogCloseBtnStyles, dialogStyles } from '@/styles/dialog';

import { Overlay } from '@/ui/Dialog';
import { XIcon } from '@/ui/Icons/XIcon';

import type { TokenBase } from '@/utils/0x/token-registry.types';
import type { SEARCH_TYPES } from '@/utils/amplitude';

const TokenSelectContent = dynamic(() => import('./SelectTokenDialogContent'), {
  ssr: false,
  loading: () => (
    <Dialog.Content
      className={dialogStyles({
        size: 'large',
        class: 'flex items-center justify-center tablet:h-[730px] transition-none!',
      })}
      aria-describedby={undefined}
      onCloseAutoFocus={(event) => event.preventDefault()}
    >
      <VisuallyHidden>
        <Dialog.Title>Search tokens</Dialog.Title>
      </VisuallyHidden>
      <Dialog.Close
        aria-label="Close"
        className={dialogCloseBtnStyles().button({ absolute: true })}
      >
        <XIcon aria-hidden className={dialogCloseBtnStyles().icon()} />
      </Dialog.Close>
    </Dialog.Content>
  ),
});

type SelectTokenDialogProps = {
  chainId?: number;
  onSelect: (token: TokenBase) => void;
  searchType: SEARCH_TYPES;
  supportedChains?: Chain[];
  filterToken?: SelectTokenFilter;
  children: ReactElement;
};

export const SelectTokenDialog = ({
  chainId,
  onSelect,
  searchType,
  supportedChains,
  filterToken,
  children,
}: SelectTokenDialogProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const handleSelect = useCallback(
    (token: TokenBase) => {
      onSelect(token);
      setOpen(false);
    },
    [onSelect],
  );

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Dialog.Portal>
        <Overlay ref={overlayRef}>
          <TokenSelectContent
            chainId={chainId}
            supportedChains={supportedChains}
            searchType={searchType}
            isOpen={open}
            filterToken={filterToken}
            onSelect={handleSelect}
          />
        </Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
